import { Component, OnInit, OnDestroy } from '@angular/core';
import { PwaService } from '@lfg-services/pwa/pwa.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-pwa-update',
  template: `
    <div *ngIf="updateAvailable && isPWAInstalled" class="pwa-update-banner">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-md-8">
            <p class="mb-0">
              <i class="bi bi-arrow-clockwise me-2"></i>
              Una nueva versión de la aplicación ya está disponible y lista para descargar.
            </p>
          </div>
          <div class="col-md-4 text-end">
            <button class="btn btn-primary btn-sm me-2" (click)="updateApp()" [disabled]="isUpdating">
              <i class="bi bi-download me-1" *ngIf="!isUpdating"></i>
              <i class="bi bi-arrow-clockwise me-1 spin" *ngIf="isUpdating"></i>
              {{ isUpdating ? 'Actualizando...' : 'Actualizar' }}
            </button>
            <button class="btn btn-outline-secondary btn-sm" (click)="dismissUpdate()" [disabled]="isUpdating">
              <i class="bi bi-x me-1"></i>
              Más tarde
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .pwa-update-banner {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%);
      color: white;
      padding: 0.75rem 0;
      z-index: 9999;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }

    .pwa-update-banner p {
      margin: 0;
      font-size: 0.9rem;
    }

    .pwa-update-banner .btn {
      font-size: 0.8rem;
      padding: 0.25rem 0.75rem;
    }

    .pwa-update-banner .btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .spin {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .pwa-update-banner .text-end {
        text-align: left !important;
        margin-top: 0.5rem;
      }
    }
  `]
})
export class PwaUpdateComponent implements OnInit, OnDestroy {
  updateAvailable = false;
  isPWAInstalled = false;
  isUpdating = false;
  private updateSubscription: Subscription = new Subscription();
  private pwaStatusSubscription: Subscription = new Subscription();

  constructor(private pwaService: PwaService) {}

  ngOnInit(): void {
    // Verificar si está en modo PWA
    this.isPWAInstalled = this.pwaService.isPWAInstalled();

    // Suscribirse a cambios en el estado PWA
    this.pwaStatusSubscription.add(
      this.pwaService.getPWAStatusObservable().subscribe((isPWA: boolean) => {
        this.isPWAInstalled = isPWA;
      })
    );

    // Suscribirse a actualizaciones disponibles
    this.updateSubscription.add(
      this.pwaService.getUpdateAvailableObservable().subscribe((available: boolean) => {
        this.updateAvailable = available;
      })
    );

    // Verificar actualizaciones al inicializar
    this.checkForUpdates();
  }

  private async checkForUpdates(): Promise<void> {
    try {
      await this.pwaService.checkForUpdate();
    } catch (error) {
      console.warn('No se pudo verificar actualizaciones:', error);
    }
  }

  async updateApp(): Promise<void> {
    if (this.isUpdating) return;

    this.isUpdating = true;

    try {
      await this.pwaService.activateUpdate();
      // El servicio debería recargar la página automáticamente
    } catch (error) {
      console.error('Error al actualizar la aplicación:', error);
      this.isUpdating = false;

      // Mostrar mensaje de error al usuario
      alert('Error al actualizar la aplicación. Por favor, recarga la página manualmente.');
    }
  }

  dismissUpdate(): void {
    this.pwaService.clearUpdateAvailable();
  }

  ngOnDestroy(): void {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
    if (this.pwaStatusSubscription) {
      this.pwaStatusSubscription.unsubscribe();
    }
  }
}
